import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  input,
  Input,
  linkedSignal, output,
  Output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { isNullOrEmpty } from '@softline/core';
import { UiCoreModule, VirtualKeyboardDirective } from '@softline/ui-core';
import { MengenEingabeFormatPipe } from './mengen-eingabe-format.pipe';

const VALID_CHARS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0', '.', ','];

@Component({
  selector: 'soft-mengen-eingabe',
  imports: [CommonModule, UiCoreModule, MengenEingabeFormatPipe],
  templateUrl: './mengen-eingabe.component.html',
  styleUrls: ['./mengen-eingabe.component.css'],
  hostDirectives: [
    {
      directive: VirtualKeyboardDirective,
      inputs: ['softVirtualKeyboardEnabled', 'softVirtualKeyboardTrigger'],
      outputs: [
        'softVirtualKeyboardEnter',
        'softVirtualKeyboardAppear',
        'softVirtualKeyboardDisappear',
        'softVirtualKeyboardBackdropClick',
        'softVirtualKeyboardInput',
        'softVirtualKeyboardChange:softVirtualKeyboardValueChange',
      ],
    },
  ],
})
export class MengenEingabeComponent {
  defaultIncrementFn = () => {
    const currentValue = this.value;
    this.setValue(currentValue + 1);
    if (currentValue !== this.value) this.emitValueChange(this.value);
  };

  defaultDecrementFn = (input?: HTMLInputElement) => {
    const currentValue = this.value;
    this.setValue(currentValue - 1);

    if (currentValue !== this.value) this.emitValueChange(this.value);

    if (input && (this.value === 0 || !this.value)) {
      this.resetToZero(input);
    }
  };

  defaultDecrementDisabledFn = () =>
    (this.value <= 0 && !this.negativeValuesValid) ||
    (this.minValue !== undefined &&
      this.minValue !== null &&
      this.value <= (this.minValue ?? 0));

  defaultIncrementDisabledFn = () =>
    !!this.value && this.value >= (this.maxValue ?? 99999999);

  @Input()
  get value(): number {
    return this._value();
  }
  set value(value: number) {
    this._value.set(value);
  }

  @Input() maxValue: number | null | undefined;
  @Input() minValue: number | null | undefined;
  @Output() valueChange = new EventEmitter<number>();
  @Input() einheit?: string;

  readonly _float = input<boolean>(false, {
    alias: 'float'
  });

  @Input() disabled = false;
  @Input() inputClass: string | null = null;
  @Input() inputFieldClass: string | null = null;
  @Input() inputStyle: { [p: string]: any } | null = null;
  @Input() negativeValuesValid = false;
  @Input() showMinValue = false;

  get float(): boolean {
    return this._float();
  };

  changedValue: number | null = null;

  readonly incrementFn = input(this.defaultIncrementFn);
  readonly incrementDisabledFn = input(this.defaultIncrementDisabledFn);
  readonly incrementButtonClass = input<string>('');
  readonly decrementFn = input(this.defaultDecrementFn);
  readonly decrementDisabledFn = input(this.defaultDecrementDisabledFn);
  readonly decrementButtonClass = input<string>('');

  readonly customClass = input<string>('');
  readonly size = input<'auto' | 'default' | 'small'>('default');

  enterPress = output<number>()

  readonly _value = signal<number>(0);
  readonly _displayedValue = linkedSignal<string>(() => `${this._value()}`);

  readonly sizeClass = computed(() => {
    switch (this.size()) {
      case 'small':
        return 'h-14';
      case 'auto':
        return 'h-auto';
      case 'default':
        return 'h-20';
    }
  });

  preventKeyboardEventEmission = input(false);

  readonly inputElement = viewChild('input', { read: ElementRef<HTMLInputElement> });
  readonly focused = signal(false);

  private commaPressed = false;

  readonly keyboardInputType = computed(() => {return this._float() ? 'decimal' : 'number'})

  constructor() {
    inject(VirtualKeyboardDirective).register({
      element: this.inputElement,
      inputType: this.keyboardInputType,
      value: computed(() => {
        const value = this._value();
        return value !== undefined && value !== null && !isNaN(value) ? `${value}` : null;
      }),
      onEnter: (_, closeFn) => {
        const inputElement = untracked(() => this.inputElement()?.nativeElement);
        console.log('onEnter', inputElement);
        if (!inputElement) this.onBlur(inputElement);
        closeFn();
      },
      onDismiss: (value) => {
        const formattedInput = this.formatUserInputString(value);

        if (!this.isValidKey(value)) {
          const inputElement = untracked(() => this.inputElement());

          const validValue = this.removeInvalidCharacters(value);

          this.setInputElementValue(
            inputElement?.nativeElement,
            value !== formattedInput
              ? this.formatToDisplayableValue(validValue)
              : validValue
          );

          this.value = Math.max(this.minValue ?? 0, Number(validValue));
          this.emitValueChange(this.value);
        } else {
          const numericValue = Number(formattedInput);

          if (isNaN(numericValue)) {
            return;
          }

          this.setValue(numericValue);
          this.emitValueChange(this.value ?? 0);

          const elem = this.inputElement()?.nativeElement;

          // If setValue changes the value (validation to min/max value), then we need to update the input elements value
          if (numericValue !== this.value && elem) {
            this.setInputElementValue(
              elem,
              this.formatToDisplayableValue(this.value + '')
            );
          }
        }
      },
      valueChange: (value) => {
        this._displayedValue.set(this.formatUserInputString(value) ?? '');
      },
    });
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (this.preventKeyboardEventEmission()) {
      event.stopPropagation();
      event.stopImmediatePropagation();
      return;
    }

    // when the input is focused then we skip the keydown event logic
    // this is required in order to support android software keyboard input by using the "input" event
    // reason for using "input" instead of "keydown": keydown emits "Unidentified" on android software keyboards
    if (this.focused()) {
      return;
    }

    if (event.key === 'Unidentified' || event.key === '') {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (event.key === 'ArrowUp') {
      this.increment();
    } else if (event.key === 'ArrowDown') {
      this.decrement();
    } else {
      const inputElement = this.inputElement();

      if (!inputElement) return;

      let value = inputElement.nativeElement?.value ?? '';

      if (this.commaPressed) {
        this.commaPressed = false;
      }

      if (event.key === 'Backspace' && value.length > 0) {
        value = value.substring(0, value.length - 1);
      } else if (event.key === '.' || event.key === ',') {
        this.commaPressed = true;
        this.setInputElementValue(inputElement.nativeElement, `${value},`);
      } else {
        value += this.commaPressed ? `.${value}` : event.key;
        this.commaPressed = false;
      }

      this.onInput(event, value);
    }
  }

  onInput(event: Event, value: string | null): void {
    if (this.disabled) return;

    const formattedInput = this.formatUserInputString(value);

    if (!this.isValidKey(value)) {
      const validValue = this.removeInvalidCharacters(formattedInput);

      this.setInputElementValue(
        event.target as HTMLInputElement,
        value !== formattedInput ? this.formatToDisplayableValue(validValue) : validValue
      );

      this.value = Math.max(this.minValue ?? 0, Number(validValue));

      this.emitValueChange(this.value);

      event.preventDefault();
      event.stopPropagation();
      return;
    }

    const numericValue = Number(formattedInput);

    if (isNaN(numericValue)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    this.setValue(numericValue);
    this.emitValueChange(this.value ?? 0);

    const elem = this.inputElement()?.nativeElement;

    // If setValue changes the value (validation to min/max value) then we need to update the input elements value
    if (numericValue !== this.value && elem) {
      this.setInputElementValue(elem, this.formatToDisplayableValue(this.value + ''));
    }
  }

  onFocus(input: HTMLInputElement): void {
    this.focused.set(true);
    this.highlightInputText(input);
  }

  onEnterWhenFocused(event: Event, input: HTMLInputElement): void {
    event.stopPropagation();
    input.blur()
    this.enterPress.emit(this.value)
  }

  onBlur(input: HTMLInputElement): void {
    this.focused.set(false);

    if (this.value === 0 || isNaN(this.value) || isNullOrEmpty(input.value)) {
      this.resetToZero(input);
      return;
    }

    const formattedInput = this.formatUserInputString(input.value);
    const numericValue = Number(formattedInput);

    if (isNaN(numericValue)) {
      this.resetToZero(input);
      return;
    }

    this.setValue(numericValue);

    const validValue = this.removeInvalidCharacters(formattedInput);

    this.setInputElementValue(
      input,
      input.value !== formattedInput
        ? this.formatToDisplayableValue(validValue)
        : validValue
    );
  }

  increment() {
    this.incrementFn()();
  }

  decrement(input?: HTMLInputElement): void {
    this.decrementFn()(input);
  }

  highlightInputText(input: HTMLInputElement) {
    input.setSelectionRange(0, 100000);
  }

  private isValidKey(input: string | null): boolean {
    if (!input || input.length === 0) return true;
    const entered = input[input.length - 1];
    return VALID_CHARS.includes(entered) || (this.negativeValuesValid && entered === '-');
  }

  private formatUserInputString(value: string | null): string {
    const normalizedValue = value?.replace(/[,]/g, '.') ?? '';
    const output = normalizedValue.split('.');
    const firstPart = output.shift(); // remove and return first element
    return firstPart + (output.length ? '.' : '') + output.join('');
  }

  private removeInvalidCharacters(input: string | null): string {
    let validInput = '';

    for (const char of input ?? []) {
      if (!this.isValidKey(char)) continue;
      validInput += char;
    }

    return validInput.length > 0 ? validInput : '0';
  }

  private setValue(value: number | null | undefined) {
    if (this.maxValue && (value || 0) > this.maxValue) {
      this.value = this.maxValue;
    } else if ((value || 0) <= 0 && !this.negativeValuesValid) {
      this.value =
        value !== undefined && value !== null
          ? Math.max((this.minValue ?? 0) > 0 ? (this.minValue ?? 0) : 0, value)
          : (this.minValue ?? 0);
    } else {
      this.value = this.minValue
        ? Math.max(this.minValue ?? 0, value ?? 0)
        : (value ?? 0);
    }

    if (isNaN(this.value)) {
      this.value = (this.minValue ?? 0) > 0 ? (this.minValue ?? 0) : 0;
    }
  }

  private formatToDisplayableValue(value: string): string {
    return value.replace('.', ',');
  }

  private resetToZero(input: HTMLInputElement): void {
    this.setInputElementValue(
      input,
      this.minValue && this.minValue > 0
        ? `${this.minValue}`
        : this.maxValue && this.maxValue <= 0
          ? `${this.maxValue}`
          : '0'
    );

    this.setValue(
      this.minValue && (this.minValue || 0) > 0
        ? this.minValue
        : this.maxValue && this.maxValue <= 0
          ? this.maxValue
          : 0
    );
  }

  private emitValueChange(value: number) {
    if (this.changedValue === value) return;
    this.changedValue = value;
    this.valueChange.emit(value);
  }

  private setInputElementValue(
    element: HTMLInputElement | undefined,
    value: string | number
  ): void {
    if (element) {
      element.value = typeof value === 'string' ? value : value + '';
    }
  }
}
